import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Grid,
  TableFooter,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { getBusinessData } from '../../common/utils';

interface ReceiptItem {
  orderID?: string;
  productName?: string;
  quantity?: string;
  unitPrice?: number;
  amount?: number;
  status?: string;
  description?: string;
}

interface ReceiptData {
  customer?: {
    name?: string;
    phone?: string;
    email?: string;
  };
  order?: {
    id?: string;
    date?: string;
    status?: string;
    paymentStatus?: string;
    paymentMethod?: string;
  };
  items?: ReceiptItem[];
  summary?: {
    totalAmount?: number;
    paidAmount?: number;
    balanceDue?: number;
  };
}

const ReceiptModal_Large = ({ open, onClose, receiptData }: { 
  open: boolean; 
  onClose: () => void; 
  receiptData?: ReceiptData 
}) => {
  
  const handlePrint = () => {
    const content = document.getElementById('receipt-content');
    if (!content) return;

    const printWindow = window.open('', '', 'width=900,height=650');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Receipt</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
            }
            th, td {
              padding: 8px;
              text-align: left;
              border-bottom: 1px solid #ccc;
            }
            h5 {
              margin: 0;
            }
          </style>
        </head>
        <body>
          ${content.innerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  // Fixed getSafe function
  const getSafe = <T,>(obj: any, path: string, defaultValue: T): T => {
    if (!obj) return defaultValue;
    
    const keys = path.split('.');
    let result = obj;
    
    for (const key of keys) {
      if (result === null || result === undefined) {
        return defaultValue;
      }
      result = result[key];
    }
    
    return result !== undefined ? result : defaultValue;
  };

  // Calculate item amount from quantity and unit price if amount is 0 or not provided
  const calculateItemAmount = (item: ReceiptItem): number => {
    // If amount is provided and not 0, use it
    if (item.amount && item.amount > 0) {
      return item.amount;
    }
    
    // Otherwise calculate from quantity and unit price
    const quantity = Number(item.quantity) || 1;
    const unitPrice = Number(item.unitPrice) || 0;
    return quantity * unitPrice;
  };

  // Helper function to calculate totals from items if summary is not provided
  const calculateTotals = () => {
    if (!receiptData || !receiptData.items) {
      return { totalAmount: 0, paidAmount: 0, balanceDue: 0 };
    }

    // Calculate total from items
    const totalAmount = receiptData.items.reduce((sum, item) => 
      sum + calculateItemAmount(item), 0
    );
    
    // Use provided summary values or calculate from items
    const summaryTotal = getSafe(receiptData, 'summary.totalAmount', 0);
    const finalTotalAmount = summaryTotal > 0 ? summaryTotal : totalAmount;
    
    const paidAmount = getSafe(receiptData, 'summary.paidAmount', finalTotalAmount);
    const balanceDue = getSafe(receiptData, 'summary.balanceDue', finalTotalAmount - paidAmount);

    return {
      totalAmount: finalTotalAmount,
      paidAmount,
      balanceDue
    };
  };

  const totals = calculateTotals();

  if (!receiptData) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>Receipt Error</DialogTitle>
        <DialogContent>
          <Alert severity="error">No receipt data available</Alert>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {getBusinessData().name} Payment Receipt
        <Button onClick={onClose} style={{ float: 'right' }}>
          Close
        </Button>
      </DialogTitle>
      <DialogContent dividers>
        <Box id="receipt-content" sx={{ p: 2 }}>
          {/* Header */}
          <Box sx={{ flexGrow: 1, p: 3, borderBottom: '2px solid #ccc' }}>
            <Grid container spacing={2}>
              <Grid item xs={6} container alignItems="center">
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    bgcolor: 'primary.main',
                    mr: 2,
                    borderRadius: '50%',
                  }}
                />
                <div>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                    {getBusinessData().name}
                  </Typography>
                  <Typography variant="subtitle1">
                    Receipt
                  </Typography>
                </div>
              </Grid>
              <Grid item xs={6} container direction="column" alignItems="flex-end">
                <Typography variant="body2">
                  Date: {new Date().toLocaleString()}
                </Typography>

                {getBusinessData().address && (
                  <Box>
                    <Typography variant="caption">
                      <LocationOnIcon sx={{ fontSize: 'small', verticalAlign: 'middle' }} />  {getBusinessData().address}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption">
                    <PhoneIcon sx={{ fontSize: 'small', verticalAlign: 'middle' }} />  {getBusinessData().phone}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption">
                    <EmailIcon sx={{ fontSize: 'small', verticalAlign: 'middle' }} />
                    {getBusinessData().email}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Customer and Order Info */}
          <Table sx={{ mb: 3 }} size="small">
            <TableBody>
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Customer Information
                  </Typography>
                </TableCell>
                <TableCell colSpan={3}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Order Information
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Name:</TableCell>
                <TableCell>{getSafe(receiptData, 'customer.name', 'N/A')}</TableCell>
                <TableCell>Order ID:</TableCell>
                <TableCell colSpan={2}>{getSafe(receiptData, 'order.id', 'N/A')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Phone:</TableCell>
                <TableCell>{getSafe(receiptData, 'customer.phone', 'N/A')}</TableCell>
                <TableCell>Order Date:</TableCell>
                <TableCell colSpan={2}>{getSafe(receiptData, 'order.date', 'N/A')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Email:</TableCell>
                <TableCell>{getSafe(receiptData, 'customer.email', 'N/A')}</TableCell>
                <TableCell>Status:</TableCell>
                <TableCell>{getSafe(receiptData, 'order.status', 'N/A')}</TableCell>
                <TableCell>Payment: {getSafe(receiptData, 'order.paymentStatus', 'N/A')}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell>Method:</TableCell>
                <TableCell colSpan={2}>{getSafe(receiptData, 'order.paymentMethod', 'N/A')}</TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {/* Order Items */}
          <Typography variant="subtitle1" gutterBottom>
            Order Items ({getSafe(receiptData, 'items.length', 0)})
          </Typography>
          {receiptData.items && receiptData.items.length > 0 ? (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {receiptData.items.map((item, index) => {
                    const itemAmount = calculateItemAmount(item);
                    console.log(`Item ${index + 1}:`, item);
                    console.log(`Calculated amount for "${item.productName}":`, {
                      quantity: item.quantity,
                      unitPrice: item.unitPrice,
                      originalAmount: item.amount,
                      calculatedAmount: itemAmount
                    });
                    
                    return (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          {item.productName || 'Unknown Product'}
                          {item.description && ` - ${item.description}`}
                        </TableCell>
                        <TableCell align="right">{item.quantity || '1'}</TableCell>
                        <TableCell align="right">₦{Number(item.unitPrice || 0).toLocaleString()}</TableCell>
                        <TableCell align="right">₦{itemAmount.toLocaleString()}</TableCell>
                        <TableCell>{item.status || 'COMPLETED'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={4} align="right">
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        Subtotal:
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        ₦{totals.totalAmount.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">No items found for this order</Alert>
          )}

          {/* Order Summary */}
          <Box mt={3}>
            <Typography variant="subtitle1" gutterBottom>
              Order Summary
            </Typography>
            <Table size="small" sx={{ width: 'auto' }}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Total Amount:</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>₦{totals.totalAmount.toLocaleString()}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Amount Paid:</TableCell>
                  <TableCell>₦{totals.paidAmount.toLocaleString()}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Balance Due:</TableCell>
                  <TableCell sx={{ 
                    color: totals.balanceDue > 0 ? 'red' : 'green',
                    fontWeight: totals.balanceDue > 0 ? 'bold' : 'normal'
                  }}>
                    ₦{totals.balanceDue.toLocaleString()}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          {/* Footer */}
          <Box textAlign="center" mt={4}>
            <Typography variant="body2">
              Thank you for your business!
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              This is a computer-generated receipt and does not require a signature.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          variant="contained" 
          startIcon={<PrintIcon />} 
          onClick={handlePrint}
          disabled={!receiptData}
        >
          Print Receipt
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReceiptModal_Large;